import { useEffect } from 'react';

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  ogImage?: string;
  canonicalPath?: string;
  noindex?: boolean;
  schema?: Record<string, unknown>;
}

const DEFAULT_TITLE = 'Parcel Lead Pro — Find High-Potential Land Parcels Before Other Investors Do';
const DEFAULT_DESC = 'Data-powered land investment scoring across South Carolina and North Carolina. Get wholesale offer ranges, investment scores, and property analysis for 174,000+ live parcels across 5 counties.';
const DEFAULT_KEYWORDS = 'land parcels, land investing, wholesale land, parcel scoring, investment score, South Carolina, North Carolina, Virginia, Texas, GIS data, county records, property analysis, motivated sellers';
const OG_IMAGE = 'https://parcleadpro.com/og-image.jpg';
const SITE_URL = 'https://parcleadpro.com';

export default function SEO({
  title = DEFAULT_TITLE,
  description = DEFAULT_DESC,
  keywords = DEFAULT_KEYWORDS,
  ogImage = OG_IMAGE,
  canonicalPath = '',
  noindex = false,
  schema,
}: SEOProps) {
  useEffect(() => {
    // Update document title
    document.title = title;

    // Helper to update or create meta tags
    const setMeta = (selector: string, attribute: 'name' | 'property', value: string, content: string) => {
      let el = document.querySelector(`${selector}[${attribute}="${value}"]`) as HTMLMetaElement | null;
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attribute, value);
        document.head.appendChild(el);
      }
      el.content = content;
    };

    // Standard meta
    setMeta('meta', 'name', 'description', description);
    setMeta('meta', 'name', 'keywords', keywords);
    setMeta('meta', 'name', 'author', 'Parcel Lead Pro');

    // Robots
    setMeta('meta', 'name', 'robots', noindex ? 'noindex, nofollow' : 'index, follow');

    // Open Graph
    setMeta('meta', 'property', 'og:title', title);
    setMeta('meta', 'property', 'og:description', description);
    setMeta('meta', 'property', 'og:image', ogImage);
    setMeta('meta', 'property', 'og:url', `${SITE_URL}${canonicalPath}`);

    // Twitter
    setMeta('meta', 'name', 'twitter:title', title);
    setMeta('meta', 'name', 'twitter:description', description);
    setMeta('meta', 'name', 'twitter:image', ogImage);

    // Canonical
    let canonicalEl = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.href = `${SITE_URL}${canonicalPath}`;

    // Schema.org per-page
    if (schema) {
      const existing = document.getElementById('page-schema');
      if (existing) existing.remove();
      const script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = 'page-schema';
      script.textContent = JSON.stringify(schema);
      document.head.appendChild(script);
    }

    return () => {
      // Cleanup per-page schema on unmount
      if (schema) {
        const existing = document.getElementById('page-schema');
        if (existing) existing.remove();
      }
    };
  }, [title, description, keywords, ogImage, canonicalPath, noindex, schema]);

  return null;
}
